/**
 * This module combines the keyboard and the gamepad into the single user input
 * object the game controller ("pikavolley.js") reads.
 *
 * Both sources stay live at the same time: the keyboard keeps working exactly
 * as before whether or not a controller is connected, and a controller can be
 * picked up mid-game without touching anything.
 */
'use strict';
import { PikaUserInput } from './physics.js';
import { PikaGamepad } from './gamepad.js';

/** @typedef {import('./keyboard.js').PikaKeyboard} PikaKeyboard */

/**
 * Class representing one player's input, merged from a keyboard and a gamepad.
 */
export class PikaCombinedInput extends PikaUserInput {
  /**
   * @param {PikaKeyboard} keyboard the keyboard for this player
   * @param {number} gamepadSlot 0 for player 1, 1 for player 2
   */
  constructor(keyboard, gamepadSlot) {
    super();

    /** @type {PikaKeyboard} */
    this.keyboard = keyboard;
    /** @type {PikaGamepad} */
    this.gamepad = new PikaGamepad(gamepadSlot);
  }

  /**
   * Get xDirection, yDirection, powerHit input from both sources and freeze it
   * for the duration of one game frame.
   *
   * The keyboard wins on the directions when it is being held, so that a
   * controller resting slightly off-centre cannot fight a keyboard player.
   * Power hit is edge-detected separately by each source, so either one firing
   * counts as a hit.
   */
  getInput() {
    this.keyboard.getInput();
    this.gamepad.getInput();

    this.xDirection =
      this.keyboard.xDirection !== 0
        ? this.keyboard.xDirection
        : this.gamepad.xDirection;
    this.yDirection =
      this.keyboard.yDirection !== 0
        ? this.keyboard.yDirection
        : this.gamepad.yDirection;
    this.powerHit =
      this.keyboard.powerHit === 1 || this.gamepad.powerHit === 1 ? 1 : 0;
  }

  /**
   * Subscribe the keyboard event listeners of this input.
   * (The gamepad is polled, so it has nothing to subscribe.)
   */
  subscribe() {
    this.keyboard.subscribe();
  }

  /**
   * Unsubscribe the keyboard event listeners of this input.
   */
  unsubscribe() {
    this.keyboard.unsubscribe();
  }
}
